'use client';

import { FunctionalIcon } from '@/app/Components/FunctionalIcon';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import type { Slot } from './plannerModel';
import { formatCode, programColor, shortCode } from './plannerModel';
import { usePlanner } from './plannerContext';

export function useIsMobile(): boolean {
  const [mobile, setMobile] = useState(false);
  useEffect(() => {
    const query = window.matchMedia('(max-width: 767px)');
    const update = () => setMobile(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);
  return mobile;
}

/** One requirement slot: solid when earned, hatched when planned, dashed when open. */
export function SlotMark({ slot, size = 'sm' }: { slot: Slot; size?: 'sm' | 'lg' }) {
  const dims = size === 'lg' ? 'h-5 w-5 rounded-control' : 'h-3.5 w-3.5 rounded';
  if (slot.state === 'earned') return <span className={`${dims} inline-block flex-none border-[1.5px] border-[var(--ss-positive)] bg-[var(--ss-positive)]`} aria-hidden="true" />;
  if (slot.state === 'planned') return <span className={`${dims} planner-slot-planned inline-block flex-none border-[1.5px]`} aria-hidden="true" />;
  return <span className={`${dims} inline-block flex-none border-[1.5px] border-dashed border-[var(--ss-text-muted)] opacity-60`} aria-hidden="true" />;
}

export function SlotRow({ slots, size = 'sm', label }: { slots: Slot[]; size?: 'sm' | 'lg'; label?: string }) {
  const earned = slots.filter((s) => s.state === 'earned').length;
  const planned = slots.filter((s) => s.state === 'planned').length;
  const spoken = label ?? `${earned} earned, ${planned} planned, ${slots.length - earned - planned} open of ${slots.length}`;
  return (
    <span className="inline-flex flex-wrap gap-[3px]" role="img" aria-label={spoken}>
      {slots.map((slot, i) => <SlotMark key={i} slot={slot} size={size} />)}
    </span>
  );
}

export function CreditMeter({ earned, planned, needed }: { earned: number; planned: number; needed: number }) {
  const total = Math.max(needed, earned + planned, 1);
  return (
    <span className="flex h-2 w-full overflow-hidden rounded-full bg-[var(--ss-surface-subtle)] ring-1 ring-inset ring-[var(--ss-border)]" role="img" aria-label={`${earned} earned and ${planned} planned of ${needed} credits`}>
      <span className="h-full bg-[var(--ss-positive)]" style={{ width: `${(earned / total) * 100}%` }} />
      <span className="planner-slot-planned h-full" style={{ width: `${(planned / total) * 100}%` }} />
    </span>
  );
}

export function ProgramDot({ index, className = '' }: { index: number; className?: string }) {
  return <span className={`inline-block h-2 w-2 flex-none rounded-full ${className}`} style={{ backgroundColor: programColor(index) }} aria-hidden="true" />;
}

export function StatusPill({ tone, children }: { tone: 'earned' | 'planned' | 'missing' | 'neutral' | 'info'; children: React.ReactNode }) {
  const tones = {
    earned: 'bg-[var(--ss-positive-bg)] text-[var(--ss-positive)]',
    planned: 'bg-[var(--ss-warning-bg)] text-[var(--ss-warning)]',
    missing: 'bg-[var(--ss-critical-bg)] text-[var(--ss-critical)]',
    info: 'bg-[var(--ss-info-bg)] text-[var(--ss-info)]',
    neutral: 'bg-[var(--ss-surface-subtle)] text-[var(--ss-text-muted)] ring-1 ring-inset ring-[var(--ss-border)]',
  } as const;
  return <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${tones[tone]}`}>{children}</span>;
}

export function SectionLabel({ children }: { children: React.ReactNode }) {
  return <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-[var(--ss-text-muted)]">{children}</p>;
}

/**
 * Course card used on the term board and in mobile lists. The left accent is the first
 * program the course counts toward; flags mark plan issues, double counting and courses
 * that don't count toward any selected program.
 */
export function CourseCard({
  code,
  title,
  credits,
  programIndexes,
  hasIssue,
  selected,
  relation,
  dimmed,
  earned,
  onOpen,
  draggableProps,
}: {
  code: string;
  title: string;
  credits: number;
  programIndexes: number[];
  hasIssue?: boolean;
  selected?: boolean;
  relation?: 'prerequisite' | 'unlocks' | null;
  dimmed?: boolean;
  earned?: boolean;
  onOpen: (el: HTMLElement) => void;
  draggableProps?: React.HTMLAttributes<HTMLButtonElement> & { draggable?: boolean };
}) {
  const accent = programIndexes.length ? programColor(programIndexes[0]) : 'var(--ss-border)';
  const multi = new Set(programIndexes).size > 1;
  const ring = selected
    ? 'border-[var(--ss-brand)] ring-2 ring-[color-mix(in_srgb,var(--ss-brand)_25%,transparent)]'
    : relation === 'prerequisite'
      ? 'border-[var(--ss-warning)] ring-2 ring-[color-mix(in_srgb,var(--ss-warning)_30%,transparent)]'
      : relation === 'unlocks'
        ? 'border-[var(--ss-info)] ring-2 ring-[color-mix(in_srgb,var(--ss-info)_25%,transparent)]'
        : 'border-[var(--ss-border)] hover:border-[var(--ss-text-muted)]';
  return (
    <button
      type="button"
      onClick={(event) => onOpen(event.currentTarget)}
      aria-pressed={selected}
      aria-label={`${title}, ${formatCode(code)}, ${credits} credits${hasIssue ? ', has a plan issue' : ''}${earned ? ', earned' : ''}`}
      className={`group relative flex w-full flex-col gap-1 rounded-control border py-2 pl-3.5 pr-2.5 text-left transition-[opacity,box-shadow,border-color] duration-150 motion-reduce:transition-none ${earned ? 'bg-[var(--ss-surface-subtle)]' : 'bg-[var(--ss-surface)]'} ${ring} ${dimmed ? 'opacity-30' : ''}`}
      {...draggableProps}
    >
      <span className="absolute bottom-2 left-0 top-2 w-[3px] rounded-r" style={{ backgroundColor: accent }} aria-hidden="true" />
      <span className="text-[13px] font-semibold leading-snug text-[var(--ss-text)]">
        {title}
        {earned ? <span className="text-[var(--ss-positive)]" aria-hidden="true"> <FunctionalIcon name="complete" /></span> : null}
      </span>
      <span className="flex items-center justify-between gap-2 text-[11.5px] text-[var(--ss-text-muted)]">
        <span className="font-mono tabular-nums">{shortCode(code)} · {credits || '?'} cr</span>
        <span className="inline-flex gap-1">
          {hasIssue ? <span className="rounded-full bg-[var(--ss-critical-bg)] px-1.5 text-[10.5px] font-bold text-[var(--ss-critical)]" title="Plan issue">!</span> : null}
          {multi ? <span className="rounded-full bg-[var(--ss-info-bg)] px-1.5 text-[10.5px] font-bold text-[var(--ss-info)]" title="Counts toward more than one program">×2</span> : null}
          {!programIndexes.length && !earned ? <span className="rounded-full px-1.5 text-[10.5px] font-bold text-[var(--ss-text-muted)] ring-1 ring-inset ring-[var(--ss-border)]" title="Doesn't count toward a selected program">—</span> : null}
        </span>
      </span>
    </button>
  );
}

export function PlaceholderCard({ label, credits, programIndex, dimmed, onOpen, draggableProps }: {
  label: string;
  credits: number;
  programIndex: number | null;
  dimmed?: boolean;
  onOpen: (el: HTMLElement) => void;
  draggableProps?: React.HTMLAttributes<HTMLButtonElement> & { draggable?: boolean };
}) {
  return (
    <button
      type="button"
      onClick={(event) => onOpen(event.currentTarget)}
      className={`relative flex w-full flex-col gap-1 rounded-control border border-dashed border-[var(--ss-text-muted)]/50 bg-transparent py-2 pl-3.5 pr-2.5 text-left transition-opacity hover:border-[var(--ss-brand)] motion-reduce:transition-none ${dimmed ? 'opacity-30' : ''}`}
      {...draggableProps}
    >
      <span className="absolute bottom-2 left-0 top-2 w-[3px] rounded-r" style={{ backgroundColor: programIndex === null ? 'var(--ss-border)' : programColor(programIndex) }} aria-hidden="true" />
      <span className="text-[13px] font-semibold leading-snug text-[var(--ss-text)]">{label}</span>
      <span className="text-[11.5px] text-[var(--ss-text-muted)]">about {credits} cr · choose a course later</span>
    </button>
  );
}

/** A suggested (not yet accepted) course or placeholder from auto-fill. Not interactive. */
export function GhostCard({ title, code, detail, programIndex }: { title: string; code?: string; detail: string; programIndex: number | null }) {
  return (
    <div className="relative flex w-full flex-col gap-1 rounded-control border border-dashed border-[color-mix(in_srgb,var(--ss-brand)_45%,var(--ss-border))] bg-[var(--ss-surface)]/70 py-2 pl-3.5 pr-2.5 text-left" data-proposal-ghost="">
      <span className="absolute bottom-2 left-0 top-2 w-[3px] rounded-r opacity-60" style={{ backgroundColor: programIndex === null ? 'var(--ss-border)' : programColor(programIndex) }} aria-hidden="true" />
      <span className="text-[13px] font-semibold leading-snug text-[var(--ss-text)]"><span className="sr-only">Suggested: </span>{title}</span>
      <span className="text-[11.5px] text-[var(--ss-text-muted)]">{code ? <span className="font-mono tabular-nums">{shortCode(code)} · </span> : null}{detail}</span>
    </div>
  );
}

/**
 * Anchored popover on desktop, bottom sheet on phones. Closes on Escape and outside
 * pointer-down, and follows its anchor while the page scrolls.
 */
export function PlannerOverlayShell({ anchor, label, onClose, children, width = 360 }: {
  anchor: HTMLElement | null;
  label: string;
  onClose: () => void;
  children: React.ReactNode;
  width?: number;
}) {
  const mobile = useIsMobile();
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (mobile) return;
    const place = () => {
      const el = ref.current;
      if (!el) return;
      const h = el.offsetHeight;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const rect = anchor?.isConnected ? anchor.getBoundingClientRect() : null;
      if (!rect) { setPos({ top: Math.max(96, (vh - h) / 2), left: (vw - width) / 2 }); return; }
      const fitsRight = rect.right + 10 + width <= vw - 12;
      const fitsLeft = rect.left - 10 - width >= 12;
      let left: number;
      let top: number;
      if (fitsRight || fitsLeft) {
        left = fitsRight ? rect.right + 10 : rect.left - width - 10;
        top = rect.top - 8;
      } else {
        left = Math.min(Math.max(12, rect.left), vw - width - 12);
        top = rect.bottom + 8;
      }
      top = Math.min(Math.max(88, top), vh - h - 12);
      setPos({ top: Math.max(12, top), left });
    };
    place();
    const observer = new ResizeObserver(place);
    if (ref.current) observer.observe(ref.current);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => { observer.disconnect(); window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [anchor, mobile, width]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    const onDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (ref.current?.contains(target)) return;
      if (anchor && anchor.contains(target)) return;
      if ((target as Element).closest?.('[data-planner-keep-overlay]')) return;
      onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('pointerdown', onDown); };
  }, [anchor, onClose]);

  useEffect(() => {
    const first = ref.current?.querySelector<HTMLElement>('[data-autofocus]') || ref.current;
    first?.focus({ preventScroll: true });
    // When the overlay closes with focus inside it (Escape, Close, an action), hand focus back to
    // the card or button that opened it so keyboard users keep their place on the board.
    const overlay = ref.current;
    return () => {
      const active = document.activeElement;
      const lost = !active || active === document.body || Boolean(overlay?.contains(active)) || !active.isConnected;
      if (lost && anchor?.isConnected) anchor.focus({ preventScroll: true });
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Portaled to <body> so the app shell's stacking context (and its bottom dock) can't cover it.
  if (mobile) {
    return createPortal(
      <div className="fixed inset-0 z-[1000]" role="presentation">
        <div className="absolute inset-0 bg-[var(--ss-scrim,rgba(7,12,20,0.45))]" aria-hidden="true" />
        <div
          ref={ref}
          role="dialog"
          aria-modal="true"
          aria-label={label}
          tabIndex={-1}
          className="absolute inset-x-0 bottom-0 flex max-h-[85dvh] flex-col gap-3 overflow-y-auto rounded-t-[22px] bg-[var(--ss-surface)] px-4 pb-[calc(1.25rem+env(safe-area-inset-bottom))] pt-2 text-[13px] text-[var(--ss-text)] shadow-e3 focus:outline-none"
        >
          <div className="flex items-center justify-between">
            <span className="mx-auto h-1.5 w-10 rounded-full bg-[var(--ss-border)]" aria-hidden="true" />
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="absolute right-3 top-3 rounded-full p-1.5 text-[var(--ss-text-muted)] hover:bg-[var(--ss-surface-subtle)]"><XMarkIcon className="h-5 w-5" /></button>
          {children}
        </div>
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div
      ref={ref}
      role="dialog"
      aria-label={label}
      tabIndex={-1}
      className="fixed z-[1000] flex max-h-[min(640px,calc(100dvh-110px))] flex-col gap-3 overflow-y-auto rounded-card border border-[var(--ss-border)] bg-[var(--ss-surface)] p-4 text-[13px] text-[var(--ss-text)] shadow-e3 focus:outline-none"
      style={{ width, top: pos?.top ?? -9999, left: pos?.left ?? -9999 }}
    >
      <button type="button" onClick={onClose} aria-label="Close" className="absolute right-2.5 top-2.5 rounded-full p-1 text-[var(--ss-text-muted)] hover:bg-[var(--ss-surface-subtle)]"><XMarkIcon className="h-4 w-4" /></button>
      {children}
    </div>,
    document.body,
  );
}

export function TermChips({ onPick, currentSemesterNumber, readyOrder, label }: {
  onPick: (semesterNumber: number) => void;
  currentSemesterNumber?: number | null;
  readyOrder?: number | null;
  label?: (termName: string) => string;
}) {
  const { terms } = usePlanner();
  if (!terms.length) return <p className="text-xs text-[var(--ss-text-muted)]">Add a term to the plan first.</p>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {terms.map((term, order) => {
        const current = term.number === currentSemesterNumber;
        const early = readyOrder !== undefined && (readyOrder === null || order < readyOrder);
        return (
          <button
            key={term.number}
            type="button"
            disabled={current}
            onClick={() => onPick(term.number)}
            title={early ? 'Prerequisites may not be met by this term' : undefined}
            className={`min-h-8 rounded-control border px-2.5 text-xs font-semibold transition-colors motion-reduce:transition-none ${current ? 'border-[var(--ss-text)] bg-[var(--ss-text)] text-[var(--ss-surface)]' : 'border-[var(--ss-border)] bg-[var(--ss-surface)] text-[var(--ss-text)] hover:border-[var(--ss-brand)] hover:text-[var(--ss-brand)]'} ${early && !current ? 'opacity-55' : ''}`}
          >
            {label ? label(term.name) : term.name.replace(/20(\d\d)/, '’$1')}
          </button>
        );
      })}
    </div>
  );
}

/** After a keyboard move, put focus back on the moved card in its new term once it renders. */
export function focusMovedCard(toSemesterNumber: number, code: string | null) {
  const cardsIn = () => Array.from(document.querySelectorAll(`[data-board-column="${toSemesterNumber}"] [data-board-card]`));
  const before = cardsIn().length;
  let tries = 0;
  const attempt = () => {
    const cards = cardsIn();
    const card = code ? cards.find((c) => c.getAttribute('data-plan-code') === code) : cards.length > before ? cards[cards.length - 1] : undefined;
    const target = card?.querySelector(':scope > button');
    if (target instanceof HTMLElement) { target.focus(); return; }
    if (tries++ < 40) window.setTimeout(attempt, 50);
  };
  window.setTimeout(attempt, 0);
}
